import { useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import { Search } from "lucide-react-native";
import type { CountryCode } from "libphonenumber-js";
import { colors, typography } from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";
import { MOON_PHONE_COUNTRIES, type MoonPhoneCountry } from "../phoneCountries";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type MoonPhoneCountryPickerProps = {
  open: boolean;
  selectedCountry: CountryCode;
  onSelect: (country: MoonPhoneCountry) => void;
  appearance?: "default" | "glass";
  searchPlaceholder?: string;
  noMatchesMessage?: (query: string) => string;
  searchTestID?: string;
};

export function MoonPhoneCountryPicker({
  open,
  selectedCountry,
  onSelect,
  appearance = "default",
  searchPlaceholder = "Search country, code, or name",
  noMatchesMessage = (query) => `No match for “${query}”`,
  searchTestID,
}: MoonPhoneCountryPickerProps) {
  const isGlass = appearance === "glass";
  const searchFontSize = useInputFontSize();
  const searchInputRef = useRef<TextInput | null>(null);
  const capturePress = usePressCapture("MoonPhoneCountryPicker");
  const [countryQuery, setCountryQuery] = useState("");

  useEffect(() => {
    if (!open) {
      setCountryQuery("");
      return;
    }

    searchInputRef.current?.focus();
  }, [open]);

  const filteredCountries = useMemo(() => {
    const query = countryQuery.trim().toLowerCase();
    if (!query) return MOON_PHONE_COUNTRIES;
    return MOON_PHONE_COUNTRIES.filter(
      (candidate) =>
        candidate.name.toLowerCase().includes(query) ||
        candidate.iso.toLowerCase().includes(query) ||
        candidate.dialCode.includes(query),
    );
  }, [countryQuery]);

  if (!open) return null;

  return (
    <View
      style={{
        marginTop: 6,
        borderRadius: isGlass ? 18 : 10,
        borderWidth: 1,
        borderColor: isGlass ? "rgba(255, 239, 225, 0.15)" : "rgba(0,0,0,0.12)",
        borderTopColor: isGlass ? "rgba(255, 244, 236, 0.28)" : undefined,
        borderBottomColor: isGlass ? "rgba(255, 225, 207, 0.10)" : undefined,
        backgroundColor: isGlass ? "rgba(15, 11, 9, 0.24)" : "#FFFFFF",
        overflow: "hidden",
        maxHeight: isGlass ? undefined : 280,
        ...(!isGlass
          ? Platform.select({
              ios: {
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.08,
                shadowRadius: 12,
              },
              android: { elevation: 6 },
              web: { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
            })
          : {}),
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingHorizontal: isGlass ? 14 : 12,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: isGlass
            ? "rgba(255, 239, 225, 0.12)"
            : colors.border,
        }}
      >
        <Search
          size={isGlass ? 15 : 13}
          color={isGlass ? "rgba(255, 255, 255, 0.58)" : colors.textFaint}
          strokeWidth={isGlass ? 1.7 : 1.5}
        />
        <TextInput
          ref={searchInputRef}
          testID={searchTestID}
          value={countryQuery}
          onChangeText={setCountryQuery}
          placeholder={searchPlaceholder}
          placeholderTextColor={
            isGlass ? "rgba(255, 255, 255, 0.48)" : colors.textFaint
          }
          autoCorrect={false}
          spellCheck={false}
          autoFocus
          style={{
            flex: 1,
            fontSize: isGlass ? 14 : searchFontSize,
            color: isGlass ? "rgba(255, 255, 255, 0.92)" : colors.text,
            fontFamily: typography.families.sans,
            ...(Platform.OS === "web" ? { outlineStyle: "none" } : null),
          }}
        />
      </View>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
        showsVerticalScrollIndicator={false}
        style={isGlass ? { maxHeight: 280 } : undefined}
      >
        {filteredCountries.map((country, index) => {
          const isSelected = country.iso === selectedCountry;
          return (
            <Pressable
              key={country.iso}
              onPress={() => {
                capturePress(
                  pressLabel(`${country.name} ${country.dialCode}`),
                  "select_phone_country",
                );
                onSelect(country);
                setCountryQuery("");
              }}
            >
              {({ pressed }) => (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: isGlass ? 16 : 12,
                    paddingVertical: isGlass ? 10 : 7,
                    gap: isGlass ? 10 : undefined,
                    borderTopWidth: isGlass && index > 0 ? 1 : 0,
                    borderTopColor: isGlass
                      ? "rgba(255, 239, 225, 0.12)"
                      : undefined,
                    backgroundColor: isSelected
                      ? isGlass
                        ? "rgba(255, 255, 255, 0.06)"
                        : `${colors.warm}10`
                      : pressed
                        ? isGlass
                          ? "rgba(255, 255, 255, 0.04)"
                          : "rgba(0,0,0,0.04)"
                        : "transparent",
                  }}
                >
                  <View
                    style={{
                      height: 18,
                      width: isGlass ? 16 : 18,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: isGlass ? 16 : 14,
                        lineHeight: isGlass ? undefined : 18,
                        textAlign: "center",
                        transform: isGlass ? undefined : [{ translateY: 2 }],
                      }}
                      accessibilityElementsHidden
                    >
                      {country.flag}
                    </Text>
                  </View>
                  <Text
                    style={{
                      flex: 1,
                      fontFamily: typography.families.sans,
                      fontSize: isGlass ? 14 : 13,
                      lineHeight: isGlass ? undefined : 16,
                      color: isGlass
                        ? "rgba(255, 255, 255, 0.90)"
                        : isSelected
                          ? colors.text
                          : colors.textMid,
                      fontWeight: isGlass ? "400" : isSelected ? "600" : "400",
                      marginLeft: isGlass ? 0 : 8,
                    }}
                    numberOfLines={1}
                  >
                    {country.name}
                  </Text>
                  <Text
                    style={{
                      fontFamily: typography.families.sansMedium,
                      fontSize: isGlass ? 14 : 12,
                      lineHeight: isGlass ? undefined : 16,
                      color: isGlass
                        ? "rgba(255, 255, 255, 0.62)"
                        : isSelected
                          ? colors.warm
                          : colors.textFaint,
                      fontWeight: isGlass ? "400" : "500",
                      flexShrink: 0,
                      marginLeft: isGlass ? 0 : 8,
                    }}
                  >
                    {country.dialCode}
                  </Text>
                </View>
              )}
            </Pressable>
          );
        })}
        {filteredCountries.length === 0 ? (
          <View
            style={{
              paddingHorizontal: isGlass ? 16 : 12,
              paddingVertical: 14,
            }}
          >
            <Text
              style={{
                textAlign: "center",
                color: isGlass ? "rgba(255, 255, 255, 0.62)" : colors.textFaint,
                fontFamily: typography.families.sans,
                fontSize: isGlass ? 13 : 11,
                lineHeight: isGlass ? undefined : 17,
              }}
            >
              {noMatchesMessage(countryQuery)}
            </Text>
          </View>
        ) : null}
      </ScrollView>
    </View>
  );
}
